import { useState, useEffect, useCallback, useRef } from 'react';
import { COMMODITIES } from './config';

export function useCommodityData(pollingInterval = 5000) {
  const [data, setData] = useState({
    usdInr: null,
    usdInrChange: null,
    usdInrChangePct: null,
    prices: {},
    lastUpdate: null,
    isLoading: true,
    errors: {},
  });

  const isFetchingRef = useRef(false);

  // ── YAHOO FINANCE via local CRA proxy ──
  const fetchYahooQuote = async (symbol) => {
    try {
      // Routes through src/setupProxy.js → https://query1.finance.yahoo.com
      const url = `/api/yahoo/v8/finance/chart/${encodeURIComponent(symbol)}?interval=1d&range=1d`;
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), 8000);
      const resp = await fetch(url, { signal: controller.signal });
      clearTimeout(id);
      if (!resp.ok) return null;
      const json = await resp.json();
      const result = json?.chart?.result?.[0];
      if (!result) return null;
      const meta = result.meta;
      const price = meta.regularMarketPrice;
      const prevClose = meta.chartPreviousClose || meta.previousClose;
      const change = prevClose ? price - prevClose : 0;
      const changePct = prevClose ? (change / prevClose) * 100 : 0;
      return { price, change, changePct };
    } catch (e) {
      return null;
    }
  };

  const fetchWithFallbacks = async (primarySymbol, fallbackSymbols = []) => {
    const all = [primarySymbol, ...fallbackSymbols].filter(Boolean);
    for (const sym of all) {
      const result = await fetchYahooQuote(sym);
      if (result) return result;
    }
    return null;
  };

  // ── USD/INR via local CRA proxy ──
  const fetchUsdInr = async () => {
    try {
      // Routes through src/setupProxy.js → https://open.er-api.com
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), 6000);
      const resp = await fetch('/api/forex/v6/latest/USD', { signal: controller.signal });
      clearTimeout(id);
      if (resp.ok) {
        const json = await resp.json();
        if (json?.rates?.INR) {
          return { rate: json.rates.INR, source: 'ExchangeRate API' };
        }
      }
    } catch (e) { }

    // Fallback: get USD/INR from Yahoo Finance
    const yahoo = await fetchYahooQuote('USDINR=X');
    if (yahoo) {
      return { rate: yahoo.price, change: yahoo.change, changePct: yahoo.changePct, source: 'Yahoo Finance' };
    }
    return null;
  };

  // ── LME Metals via local CRA proxy ──
  const fetchLMEPrices = async () => {
    try {
      // Routes through src/setupProxy.js → https://metals.live
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), 6000);
      const resp = await fetch('/api/metals/api/v1/spot', { signal: controller.signal });
      clearTimeout(id);
      if (resp.ok) {
        const json = await resp.json();
        if (Array.isArray(json)) {
          const result = {};
          for (const item of json) {
            const name = (item.name || '').toLowerCase();
            if (name.includes('zinc')) result.zinc = { price: item.price, change: 0, changePct: 0, isLME: true };
            else if (name.includes('nickel')) result.nickel = { price: item.price, change: 0, changePct: 0, isLME: true };
            else if (name.includes('lead')) result.lead = { price: item.price, change: 0, changePct: 0, isLME: true };
          }
          if (Object.keys(result).length > 0) return result;
        }
      }
    } catch (e) { }

    // Fallback: approximate values
    return {
      zinc:   { price: 2780,  change: 0, changePct: 0, isApprox: true },
      nickel: { price: 15400, change: 0, changePct: 0, isApprox: true },
      lead:   { price: 1960,  change: 0, changePct: 0, isApprox: true },
    };
  };

  const fetchAllPrices = useCallback(async (overrideLock = false) => {
    if (isFetchingRef.current && !overrideLock) return;
    isFetchingRef.current = true;

    const commodityKeys = Object.keys(COMMODITIES);
    const yahooKeys = commodityKeys.filter(k => !COMMODITIES[k].lmeOnly);
    const lmeKeys   = commodityKeys.filter(k =>  COMMODITIES[k].lmeOnly);

    const promises = [
      fetchUsdInr(),
      ...yahooKeys.map(async (key) => {
        const config = COMMODITIES[key];
        const result = await fetchWithFallbacks(config.yahooSymbol, config.yahooFallbacks || []);
        return [key, result];
      }),
      lmeKeys.length > 0 ? fetchLMEPrices() : Promise.resolve({}),
    ];

    const results = await Promise.allSettled(promises);

    setData(prev => {
      const newState = { ...prev, isLoading: false, lastUpdate: Date.now() };

      // USD/INR
      const usdInrResult = results[0];
      if (usdInrResult.status === 'fulfilled' && usdInrResult.value) {
        newState.usdInr = usdInrResult.value.rate;
        newState.usdInrChange = usdInrResult.value.change || null;
        newState.usdInrChangePct = usdInrResult.value.changePct || null;
      }

      const newPrices = { ...prev.prices };
      const newErrors = { ...prev.errors };

      // Yahoo commodities
      for (let i = 1; i <= yahooKeys.length; i++) {
        const result = results[i];
        if (result.status === 'fulfilled' && result.value) {
          const [key, priceData] = result.value;
          if (priceData) {
            newPrices[key] = priceData;
            delete newErrors[key];
          } else if (!newPrices[key]) {
            newErrors[key] = 'No data';
          }
        }
      }

      // LME metals
      const lmeResult = results[1 + yahooKeys.length];
      if (lmeResult?.status === 'fulfilled' && lmeResult.value) {
        const lmeData = lmeResult.value;
        for (const key of lmeKeys) {
          if (lmeData[key]) {
            newPrices[key] = lmeData[key];
            delete newErrors[key];
          } else if (!newPrices[key]) {
            newErrors[key] = 'LME unavailable';
          }
        }
      }

      newState.prices = newPrices;
      newState.errors = newErrors;
      return newState;
    });

    isFetchingRef.current = false;
  }, []);

  useEffect(() => {
    fetchAllPrices();
    const intervalId = setInterval(fetchAllPrices, pollingInterval);
    return () => clearInterval(intervalId);
  }, [fetchAllPrices, pollingInterval]);

  return { data, forceRefresh: fetchAllPrices };
}
